<template>
    <div>
        <worksheet-general-information ref="worksheetGeneralInformation"
                                       :api-base-url="apiBaseUrl"
                                       :images-url="imagesUrl"
                                       :file-download-url="fileDownloadUrl"
                                       :current-user-account-id="currentUserAccountId"
                                       :work-item-id="workItemId"
                                       :work-item="workItem"
                                       :related-court-sitting-id="relatedCourtSittingId"
                                       :related-court-matter-id="relatedCourtMatterId"
                                       :related-interview-id="relatedInterviewId"
                                       :related-list-court-id="relatedListCourtId"
                                       :assignment-relevant-roles="assignmentRelevantRoles"
                                       :assignment-roles-with-audio-minutes="assignmentRolesWithAudioMinutes"
                                       :assignment-roles-with-round-robin-priority="assignmentRolesWithRoundRobinPriority"
                                       :all-adjournment-types="allAdjournmentTypes"
                                       :all-audio-sub-status-options="allAudioSubStatusOptions"
                                       :all-log-notes-sub-status-options="allLogNotesSubStatusOptions"
                                       :all-release-approval-sub-status-options="allReleaseApprovalSubStatusOptions"
                                       :all-deposit-sub-status-options="allDepositSubStatusOptions"
                                       :all-vetting-sub-status-options="allVettingSubStatusOptions"
                                       :portal-upload-required="portalUploadRequired"
                                       :readonly="readonly"
                                       :assignment-option="assignmentOption"
                                       :live-assignment-date="liveAssignmentDate"
                                       :live-assignment-start-time="liveAssignmentStartTime"
                                       :live-assignment-end-time="liveAssignmentEndTime"
                                       :delayed-assignment-start-date="delayedAssignmentStartDate"
                                       :delayed-assignment-due-date="delayedAssignmentDueDate"                                 
                                       :court-registry-id="courtRegistryId"
                                       :interview-organisation-id="interviewOrganisationId"
                                       :time-zone-id="timeZoneId"
                                       :default-live-assignment-response-time="defaultLiveAssignmentResponseTime"
                                       :default-delayed-assignment-response-time="defaultDelayedAssignmentResponseTime"
                                       :all-default-response-time-options="allDefaultResponseTimeOptions"
                                       :all-attachment-classification-options="mainAttachmentClassificationOptions"
                                       :all-audio-quality-options="allAudioQualityOptions"
                                       v-model="generalData"
                                       @modalOpen="setWorksheetReloadStatus">

            <template v-slot:action-buttons>
                <div class="btn-toolbar justify-content-end mt-2" role="toolbar" aria-label="Action buttons">
                    <div class="btn-group" role="group">
                        <b-button variant="outline-secondary"
                                  key="startwork"
                                  v-on:click="startWork"
                                  v-if="actionAvailable('start')">
                            <i class="fas fa-play mr-1"></i>Start Work
                        </b-button>
                        <b-button variant="outline-secondary"
                                  key="endwork"
                                  v-on:click="endWork"
                                  v-if="actionAvailable('end')">
                            <i class="fas fa-stop mr-1"></i>End Work
                        </b-button>
                        <b-button variant="outline-success"
                                  key="complete"
                                  v-on:click="openCompleteWorkItemDialog"
                                  v-if="actionAvailable('complete')">
                            <i class="fas fa-check mr-1"></i>Complete
                        </b-button>
                        <b-button :href="returnUrl" variant="outline-secondary"><i class="fas fa-arrow-left mr-1"></i>Back</b-button>
                    </div>
                </div>
            </template>
        </worksheet-general-information>

        <b-modal size="lg"
                 centered
                 title="Complete Work Item"
                 v-model="completeWorkData.dialogOpen"
                 v-on:ok.prevent="completeWorkItem">
            <b-alert :show="((completeWorkData.currentErrors != null) && (completeWorkData.currentErrors.length > 0))" variant="danger" dismissible>
                <div v-for="(error, index) in completeWorkData.currentErrors" :key="index">
                    <p>{{ error }}</p>
                </div>
            </b-alert>
            <p>Are you sure you want to mark the CHECKED orders as delivered?<br><b>This action cannot be undone.</b></p>

            <workitem-orders :api-base-url="apiBaseUrl"
                             :work-item-id="workItemId"
                             for-order-delivery
                             v-model="completeWorkData.ordersToBeDelivered"
                             :all-audio-sub-status-options="allAudioSubStatusOptions"
                             :can-view-turnarounds="generalData.permissions.canViewTurnarounds"
                             :all-log-notes-sub-status-options="allLogNotesSubStatusOptions"
                             :all-release-approval-sub-status-options="allReleaseApprovalSubStatusOptions"
                             :all-deposit-sub-status-options="allDepositSubStatusOptions"
                             :all-vetting-sub-status-options="allVettingSubStatusOptions"
                             :all-delivered-late-reason-options="allDeliveredLateReasonOptions"
                             @modalOpen="setWorksheetReloadStatus">
            </workitem-orders>
        </b-modal>
    </div>
</template>

<script>
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import axios from 'axios';
    import Visibility from 'visibilityjs';

    import { ValidationProvider, ValidationObserver } from 'vee-validate';

    import { extend, localize } from 'vee-validate';
    import { required, integer, min_value, double } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    extend('required', required);
    extend('integer', integer);
    extend('min_value', min_value);
    extend('double', double);
    localize('en', en);

    import ErrorsMixin from '../../mixins/ErrorsMixin';
    import DateTimeMixin from '../../mixins/DateTimeMixin';
    import TranscriptMixins from '../../mixins/TranscriptMixins';
    import WorksheetGeneralInformation from './WorksheetGeneralInformation.vue'
    import RecordedSegments from '../RecordedSegments.vue'
    
    Vue.use(BootstrapVue);
    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);
    Vue.component('worksheet-general-information', WorksheetGeneralInformation);
    Vue.component('recorded-segments', RecordedSegments);
    
    export default {
        name: 'GenericWorksheet',
        mixins: [ErrorsMixin, DateTimeMixin, TranscriptMixins],

        props: {
            returnUrl: {
                type: String,
                required: true
            },
            apiBaseUrl: {
                type: String,
                required: true
            },
            imagesUrl: {
                type: String,
                required: true
            },
            fileDownloadUrl: {
                type: String,
                required: true
            },
            currentUserAccountId: {
                type: String,
                required: true
            },
            workItemId: {
                type: String,
                required: true
            },
            relatedCourtSittingId: {
                type: String,
                default: null
            },
            relatedCourtMatterId: {
                type: String,
                default: null
            },
            relatedInterviewId: {
                type: String,
                default: null
            },
            relatedListCourtId: {
                type: String,
                default: null
            },
            assignmentRelevantRoles: {
                type: Array,
                required: true
            },
            assignmentRolesWithAudioMinutes: {
                type: Array,
                required: true
            },
            assignmentRolesWithRoundRobinPriority: {
                type: Array,
                required: true
            },
            allAdjournmentTypes: {
                type: Array,
                required: true
            },
            finalAdjournmentType: {
                type: Number,
                required: true
            },
            portalUploadRequired: {
                type: Boolean,
                default: false,
            },
            initialPermissions: {
                type: Object,
                default: null
            },
            refreshIntervalInMilliseconds: {
                type: Number,
                default: 5000
            },
            allAudioSubStatusOptions: {
                type: Array,
                default: null
            },
            allLogNotesSubStatusOptions: {
                type: Array,
                default: null
            },
            allReleaseApprovalSubStatusOptions: {
                type: Array,
                default: null
            },
            allDepositSubStatusOptions: {
                type: Array,
                default: null
            },
            allVettingSubStatusOptions: {
                type: Array,
                default: null
            },
            allDeliveredLateReasonOptions: {
                type: Array,
                default: null
            },
            readonly: {
                type: Boolean,
                default: false
            },
            assignmentOption: {
                type: String,
                required: false
            },
            liveAssignmentDate: {
                type: Date,
                required: false,
            },
            liveAssignmentStartTime: {
                type: Date,
                required: false,
            },
            liveAssignmentEndTime: {
                type: Date,
                required: false,
            },
            delayedAssignmentStartDate: {
                type: Date,
                required: false,
            },
            delayedAssignmentDueDate: {
                type: Date,
                required: false,
            },
            courtRegistryId: {
                type: String,
                required: false,
                default: null
            },
            interviewOrganisationId: {
                type: String,
                required: false,
                default: null
            },
            timeZoneId: {
                type: String,
                required: false,
                default: null
            },
            defaultLiveAssignmentResponseTime: {
                type: Number
            },
            defaultDelayedAssignmentResponseTime: {
                type: Number
            },
            allDefaultResponseTimeOptions: {
                type: Array,
                default: null
            },
            mainAttachmentClassificationOptions: {
                type: Array,
                default: null
            },
            allAudioQualityOptions: {
                type: Array,
                default: null
            },
        },
        data: function () {
            return {
                worksheetReloadStatus: false, // used to pause reload
                generalData: {
                    permissions: this.initialPermissions ? this.initialPermissions : {},
                    assignedStaff: []
                },
                workItem: null,
                completeWorkData: {
                    ordersToBeDelivered: null,
                    currentErrors: [],
                    dialogOpen: false
                },
                timer: null,
                reloadPending: false
            };
        },
        methods: {

            setWorksheetReloadStatus: function (anyInnerModalOpen) {
                this.worksheetReloadStatus = anyInnerModalOpen;
                this.reload();
            },

            apiControllerUrl: function (apiRelativeUrl) {
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += apiRelativeUrl;
                return url;
            },

            reload: function (reloadSubComponents = true) {
                if (this.timer != null) {
                    Visibility.stop(this.timer);
                    this.timer = null;
                }
                if (this.reloadPending) return;
                this.reloadPending = true;
                var promises = [];
                promises.push(this.loadWorkItem());
                if (reloadSubComponents) {
                    promises.push(this.$refs.worksheetGeneralInformation.reload());
                }
                Promise
                    .all(promises)
                    .then(() => {
                        this.reloadPending = false;
                        if (!this.completeWorkData.dialogOpen && !this.worksheetReloadStatus) {
                            console.log('reloading');
                            this.timer = Visibility.every(this.refreshIntervalInMilliseconds, this.reload);
                        } else {
                            console.log('reloading PAUSED');
                        }
                    })
            },

            loadWorkItem: function () {
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += 'workitem/' + this.workItemId;
                return axios
                    .get(url)
                    .then(response => {
                        this.workItem = response.data;
                        if (this.workItem.inProgressSinceDateTimeUTC) this.workItem.inProgressSinceDateTimeUTC = this.parseDateTime(this.workItem.inProgressSinceDateTimeUTC, null, true, true)
                        if (this.workItem.requiredDateTimeUTC) this.workItem.requiredDateTimeUTC = this.parseDateTime(this.workItem.requiredDateTimeUTC, null, true, true)
                        if (this.workItem.lastUpdateDateTimeUTC) this.workItem.lastUpdateDateTimeUTC = this.parseDateTime(this.workItem.lastUpdateDateTimeUTC, null, true, true)
                        if (this.workItem.completedDateTimeUTC) this.workItem.completedDateTimeUTC = this.parseDateTime(this.workItem.completedDateTimeUTC, null, true, true)
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        this.showErrorsToast(currentErrors);
                    });
            },

            actionAvailable: function (action) {
                if (this.workItem == null) return false;
                if (this.workItem.completedDateTimeUTC) return false;
                if (action == 'start') {
                    if (this.workItem.inProgressByUserAccountId != null) return false;
                    return this.generalData.permissions.canStartWork;
                } else if (action == 'end') {
                    if (this.workItem.inProgressByUserAccountId == null) return false;
                    if (this.generalData.permissions.canAlwaysEndWork) return true;
                    return (this.workItem.inProgressByUserAccountId == this.currentUserAccountId);
                } else if (action == 'complete') {
                    if (this.workItem.inProgressByUserAccountId == null) {
                        return this.generalData.permissions.canStartWork;
                    }
                    return (this.workItem.inProgressByUserAccountId == this.currentUserAccountId);
                }
                return false;
            },
            startWork: function () {
                var data = {
                    action: 'startWork'
                };
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += 'workitem/' + this.workItemId + '/status';
                axios
                    .put(url, JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.reload(false);
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        this.showErrorsToast(currentErrors);
                    });
            },
            endWork: function () {
                var data = {
                    action: 'endWork'
                };
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += 'workitem/' + this.workItemId + '/status';
                axios
                    .put(url, JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.reload(false);
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        this.showErrorsToast(currentErrors);
                    });
            },
            openCompleteWorkItemDialog: function () {
                this.completeWorkData.currentErrors = [];
                this.completeWorkData.dialogOpen = true;
            },
            completeWorkItem: function () {
                var data = {};
                this.completeWorkData.currentErrors = [];
                if ((this.completeWorkData.ordersToBeDelivered == null) ||
                    (this.completeWorkData.ordersToBeDelivered.length == 0)) {
                    this.completeWorkData.currentErrors.push("At least one order has to be selected.");
                }
                data.deliveredOrders = [];
                this.completeWorkData.ordersToBeDelivered.forEach((orderToBeDelivered) => {
                    if (orderToBeDelivered.deliveredOnTime) {
                        orderToBeDelivered.deliveredLateReason = null;
                        orderToBeDelivered.deliveredLateReasonText = null;
                    } else {
                        if ((orderToBeDelivered.deliveredLateReason == null) || (orderToBeDelivered.deliveredLateReason == "")) {
                            this.completeWorkData.currentErrors.push("A Late Reason has to be selected for each order not delivered on time.");
                        } else if (orderToBeDelivered.deliveredLateReason == 'Other') {
                            if ((orderToBeDelivered.deliveredLateReasonText == null) || (orderToBeDelivered.deliveredLateReasonText == "")) {
                                this.completeWorkData.currentErrors.push("A Late Reason Text/Comment has to be specified for each order with Late Reason 'Other'.");
                            }
                        }
                    }
                    data.deliveredOrders.push(
                        {
                            orderId: orderToBeDelivered.orderId,
                            deliveredOnTime: orderToBeDelivered.deliveredOnTime,
                            deliveredLateReason: orderToBeDelivered.deliveredLateReason,
                            deliveredLateReasonText: orderToBeDelivered.deliveredLateReasonText,
                        }
                    );
                });

                if (this.completeWorkData.currentErrors.length == 0) {
                    var url = this.apiBaseUrl;
                    if (!url.endsWith('/')) url += '/';
                    url += 'workitem/generic/' + this.workItemId + '/complete';
                    axios
                        .put(url, JSON.stringify(data),
                            {
                                headers: {
                                    'Content-Type': 'application/json'
                                }
                            })
                        .then(() => {
                            this.reload();
                        })
                        .catch((ex) => {
                            this.completeWorkData.currentErrors = this.handleAxiosException(ex);
                        })
                        .finally(() => {
                            if (this.completeWorkData.currentErrors.length == 0) {
                                this.completeWorkData.dialogOpen = false;
                            }
                        });
                }
            }
        },
        mounted: function () {
            this.reload(false); //each individual sub component will anyway load its data when mounted, no need to reload everything again when mounted
        },
       
        beforeDestroy: function () {
            if (this.timer != null) Visibility.stop(this.timer);
        }
    }
</script>
